<template>
    <div
        class="garment-body-selector-menu"
    >
        <v-alert
            v-if="msgError"
            class="mb-2"
            type="error"
            variant="flat"
            density="compact"
            @click:close="msgError = null"
            closable
        >
            {{ msgError }}
        </v-alert>
        <v-menu
            v-model="menu"
            origin="auto"
            location="bottom start"
            :close-on-content-click="false"
            :loading="isLoading"
        >
            <template #activator="{ props }">
                <v-card
                    v-bind="props"
                    variant="outlined"
                >
                    <!-- Selected Item -->
                    <template #title>
                        <GarmentBodySelectorMenuItem
                            v-if="selectedBody"
                            :body="selectedBody"
                            class="font-size-normal"
                            selected-mode
                        ></GarmentBodySelectorMenuItem>
                        <label
                            v-else
                            class="text-uppercase font-weight-bold"
                        >
                            Select a body
                        </label>
                    </template>

                    <template #append>
                        <v-icon v-if="menu" :icon="mdiChevronUp"></v-icon>
                        <v-icon v-else :icon="mdiChevronDown"></v-icon>
                    </template>
                </v-card>
            </template>

            <!-- Menu Content -->
            <v-card>
                <v-item-group
                    v-if="!isLoading && Array.isArray(bodiesItems)"
                    :model-value="selectedUid"
                    @update:model-value="onChange($event!)"
                >
                    <!-- No Bodies Found -->
                    <v-item
                        v-if="!bodiesItems.length"
                        #default="{ isSelected, toggle }"
                    >
                        <v-btn
                            color="accent"
                            variant="flat"
                            @click="onCreateBody($event), menu = false"
                            block
                        >
                            No bodies found. Click here to create one!
                        </v-btn>
                    </v-item>

                    <!-- Bodies List -->
                    <v-item
                        v-else
                        v-for="(body, index) in bodiesItems"
                        :key="index"
                        :value="body.uid"
                        #default="{ isSelected, toggle }"
                    >
                        <GarmentBodySelectorMenuItem
                            class="garment-body-selector-menu-item cursor-pointer"
                            :body="body"
                            :active="isSelected"
                            @click="toggle"
                            @edit-body="onEditBody($event)"
                        ></GarmentBodySelectorMenuItem>
                    </v-item>
                </v-item-group>
                <div
                    v-else-if="isLoading"
                    class="d-flex flex-row justify-center my-4"
                >
                    <v-progress-circular
                        size="42"
                        width="6"
                        color="accent-darken-2"
                        indeterminate
                    ></v-progress-circular>
                </div>
                <v-pagination
                    v-if="pagination.totalPages > 1"
                    v-model="pagination.page"
                    :length="pagination.totalPages"
                    :total-visible="8"
                    :disabled="isLoading"
                    show-first-last-page
                    @update:modelValue="onPageChange($event)"
                ></v-pagination>
            </v-card>
        </v-menu>

        <div
            class="pt-2 d-flex flex-row flex-grow-1 justify-space-between"
        >
            <v-btn
                to="/account/bodies"
                color="secondary"
                size="small"
                variant="text"
                nuxt
            >
                Manage Bodies
                <v-icon
                    :icon="mdiExitToApp"
                    class="ml-1"
                ></v-icon>
            </v-btn>
            <v-btn
                @click.stop="onCreateBody($event)"
                color="secondary"
                size="small"
                variant="text"
            >
                Create Body
                <v-icon
                    size="28"
                    color="primary-lighten-1"
                    :icon="mdiPlus"
                ></v-icon>
            </v-btn>
        </div>

        <ResponsiveModal v-model="dialogAddBody">
            <template #default="{ onExit }">
                <BodiesForm
                    :key="`bodies-form-${bodiesFormKeyCount}`"
                    @on-exit="onExit()"
                    @on-save="onSave($event)"
                    :user-body="currentEditingBody"
                    :in-modal="true"
                ></BodiesForm>
            </template>
        </ResponsiveModal>
    </div>
</template>

<script lang="ts" setup>
    import { useApiQueryParams, useBodies } from '~/composables';
    import { useGlobalAlert } from 'x1-base/composables';
    import BodiesForm from '~/components/account/bodies/BodiesForm/BodiesForm.vue';
    import type { UserBody } from '~/types';
    import type { PaginatedRequest } from 'x1-base/types';
    import { BodiesModel } from '~/models';
    import { isError as lo_isError } from 'es-toolkit';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    import { isNil as lo_isNil } from 'es-toolkit';
    import type { WatchStopHandle } from 'vue';
    import {
        mdiChevronUp,
        mdiChevronDown,
        mdiExitToApp,
        mdiPlus
    } from '@mdi/js';

    import GarmentBodySelectorMenuItem from '~/components/bodies/GarmentBodySelectorMenuItem/GarmentBodySelectorMenuItem.vue';

    const props = withDefaults(defineProps<{
        modelValue?: Nilish<UserBody>;
        pageSize?: number;
        id?: string;
    }>(), {
        modelValue: undefined,
        pageSize: 3,
        id: 'body-selector-menu-input'
    });

    const emit = defineEmits<{
        'update:modelValue': [value: Nilish<UserBody>];
    }>();

    const storePrefix = 'x1_body_selected';
    const useBodiesObj = useBodies();
    const menu = ref(false);
    const dialogAddBody = ref(false);
    const currentEditingBody = ref<Nullable<BodiesModel>>(null);
    const bodiesFormKeyCount = ref(0);
    const {
        paginationRequest,


        initFromQueryParams,
        pagination,
        sortItems,
        updatePagination,
        queryParamsRequest,
        hasQueryParamValues,
        clearApiQueryParams,
        getApiQueryParams,
        updateOrderingParam,
        onRequestParamsUpdate,
        requestParamsWatcherResume


    } = useApiQueryParams({
        key: 'BodySelMenuInput',
        resetOnUnload: true,
        opts: {
            page_size: props.pageSize
        },
        persistedParams: {
            page_size: props.pageSize
        }
    });
    const createGlobalAlert = useGlobalAlert();
    const msgError = ref<Nullable<string>>(null);
    const selectedUid = ref<Nullable<string>>(null);
    const selectedBody = ref<Nullable<UserBody>>(null);


    async function getItems(): Promise<BodiesModel[] | void | Error> {
        const paginatedRequest: PaginatedRequest = {
            params: getApiQueryParams()
        };
        const response = await useBodiesObj.getItems(paginatedRequest);

        if (lo_isError(response)) {
            msgError.value = 'Could not load saved bodies. Please reload page and try again';
            return response;
        } else if (response && Array.isArray(response.data)) {
            updatePagination(response.pagination);
            // @ts-ignore
            return response.data;
        }
    }

    const {
        data: bodiesItems,
        pending: isLoading,
        refresh: onReload
    } = useLazyAsyncData<BodiesModel[] | void | Error>(
        'keyBodiesList',
        () => getItems()
    );

    let unWatchBodiesItems: WatchStopHandle;
    unWatchBodiesItems = watch(
        bodiesItems,
        (newVal, oldVal) => {
            if (!lo_isError(newVal) && lo_isNil(oldVal) && !lo_isEmpty(newVal)) {
                autoSelectUser();
                unWatchBodiesItems?.();
            }
        },
        { immediate: true }
    );

    function autoSelectUser(uid?: Nilish<string>) {
        if (lo_isError(bodiesItems.value) || !Array.isArray(bodiesItems.value)) {
            return;
        }

        if (!uid && import.meta.client) {
            uid = localStorage?.getItem(getStoreKey());
        }

        const searchSelfFn = (o: BodiesModel) => o.isSelf === true;
        const searchFn = uid ? (o: BodiesModel) => o.uid === uid : searchSelfFn;

        let body = bodiesItems.value.find(searchFn);

        // Try to set self
        if (uid && !body) {
            body = bodiesItems.value.find(searchSelfFn);
        }

        setBody(body ?? null);
    }

    async function onPageChange(page: number): Promise<void> {
        updatePagination({ page });

        await nextTick();
        onReload();
    }

    async function onChange(currentSelectedUid: string) {
        let modelObj: Nilish<BodiesModel>;
        bodiesFormKeyCount.value++;

        if (
            bodiesItems.value &&
            !lo_isError(bodiesItems.value) &&
            !lo_isNil(bodiesItems.value) &&
            currentSelectedUid
        ) {
            modelObj = bodiesItems.value.find((o) => o.uid === currentSelectedUid);
        }

        persistOption(currentSelectedUid);
        setBody(modelObj ?? null);
        await nextTick();
        menu.value = false;
    }

    function setBody(body?: Nilish<UserBody | BodiesModel>) {
        const bodyObj = body ? BodiesModel.toPlainObject<Nullable<UserBody>>(body) : null;
        selectedBody.value = bodyObj;
        selectedUid.value = bodyObj?.uid ?? null;
        emit('update:modelValue', bodyObj);
    }

    async function onSave(body: Partial<UserBody>): Promise<void> {
        const { uid } = body;
        const response = uid
            ? await useBodiesObj.updateItem({ uid, data: body })
            : await useBodiesObj.createItem(body);

        bodiesFormKeyCount.value++;

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Could not process body. Please try again.',
                show: true,
                type: 'error'
            });
            return;
        }

        onExit();

        // Reload bodies list
        await onReload();
        autoSelectUser(response.uid);
    }

    function onCreateBody(e: Event) {
        currentEditingBody.value = null;
        dialogAddBody.value = true;
    }

    async function onEditBody(body?: Nullable<BodiesModel | UserBody>): Promise<void> {
        menu.value = false;
        bodiesFormKeyCount.value++;

        await nextTick();

        if (body) {
            currentEditingBody.value = body as BodiesModel;
            dialogAddBody.value = true;
        } else {
            currentEditingBody.value = null;
            dialogAddBody.value = false;
        }
    }

    function onExit(): void {
        dialogAddBody.value = false;
        currentEditingBody.value = null;
        bodiesFormKeyCount.value++;
    }

    function persistOption(uid?: Nilish<string>) {
        if (import.meta.client && uid) {
            localStorage.setItem(getStoreKey(), uid);
        }
    }

    function getStoreKey(): string {
        return `${storePrefix}-${props.id}`;
    }

    onBeforeMount(() => {
        pagination.value!.pageSize = props.pageSize;
        paginationRequest.value.page_size = props.pageSize;
    });

    onBeforeUnmount(() => {
        unWatchBodiesItems?.();
    });
</script>

<style lang="scss">
.garment-body-selector-menu {
    .v-card-title {
        font-size: unset;
        font-weight: unset;
    }

    .v-alert__close button {
        color: unset !important;
    }

    &-item {
        &:hover {
            background-color: rgba(var(--v-theme-surface-odd), var(--v-hover-opacity));
        }
    }
}
</style>
